import React, { useState } from "react";
import CalculatorCard from "../../components/CalculatorCard";
import PageSeo from "../../components/PageSeo";
import { Helmet } from "react-helmet-async";

export default function InsuranceCalculator() {
  const [bikeValue, setBikeValue] = useState("");
  const [bikeAge, setBikeAge] = useState("");
  const [cityFactor, setCityFactor] = useState("1");
  const [premium, setPremium] = useState(null);

  const calculateInsurance = () => {
    const value = Number(bikeValue);
    const age = Number(bikeAge);
    const factor = Number(cityFactor);

    if (!value || age < 0) return;

    const depreciation = Math.max(0.45, 1 - age * 0.08);
    const idv = value * depreciation;
    const estimatedPremium = idv * 0.035 * factor;

    setPremium({
      idv: Math.round(idv),
      annual: Math.round(estimatedPremium),
    });
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`Check my calculation result: IDV ₹ ${premium.idv}, Annual Premium ₹ ${premium.annual} | MotoHub India https://motohubindia.com`);
    const url = window.innerWidth < 768 
      ? `whatsapp://send?text=${text}` 
      : `https://web.whatsapp.com/send?text=${text}`;
    window.open(url, '_blank');
  };

  const downloadPDF = () => {
    const el = document.getElementById('calculator-result');
    const original = document.title;
    document.title = 'MotoHub-Calculation-Result';
    window.print();
    document.title = original;
  };

  return (
    <>
      <Helmet>
        <title>Bike Insurance Calculator India | Premium Estimator | MotoHub India</title>
        <meta
          name="description"
          content="Estimate bike insurance premium in India using MotoHub calculator. Calculate IDV and annual premium based on bike value, age, and city."
        />
        <meta
          name="keywords"
          content="bike insurance calculator India, two wheeler insurance premium, motorcycle insurance calculator India, IDV calculation bike"
        />
        <link rel="canonical" href="https://motohubindia.com/calculator/insurance" />
        <meta property="og:title" content="Bike Insurance Calculator | MotoHub India" />
        <meta property="og:description" content="Estimate your bike insurance premium easily." />
        <meta property="og:url" content="https://motohubindia.com/calculator/insurance" />
      </Helmet>

      <div style={{ padding: "30px" }}>
        <PageSeo
          title="Bike Insurance Calculator India | MotoHub"
          description="Estimate two wheeler insurance premium in India."
          path="/calculator/insurance"
        />

        <CalculatorCard title="Bike Insurance Calculator">
          <div style={seoBox}>
            <h2>Bike Insurance Calculator India</h2>
            <p style={seoText}>
              Use MotoHub India insurance calculator to estimate your two wheeler insurance premium. 
              Enter bike value, age, and city type to calculate approximate IDV and annual premium.
            </p>
            <p style={seoText}>
              This tool helps riders understand insurance costs before buying or renewing a policy, 
              making it easier to compare options and plan expenses.
            </p>
          </div>

          <input
            placeholder="Current bike value (Rs)"
            value={bikeValue}
            onChange={(e) => setBikeValue(e.target.value)}
            style={inputStyle}
          />

          <input
            placeholder="Bike age (years)"
            value={bikeAge}
            onChange={(e) => setBikeAge(e.target.value)}
            style={inputStyle}
          />

          <select
            value={cityFactor}
            onChange={(e) => setCityFactor(e.target.value)}
            style={inputStyle}
          >
            <option value="1">Tier 2 / Tier 3 city</option>
            <option value="1.08">Tier 1 city</option>
            <option value="1.15">Metro city</option>
          </select>

          <button style={buttonStyle} onClick={calculateInsurance}>
            Estimate Premium
          </button>

          <div id="calculator-result">
            {premium && (
              <>
                <h3 style={{ marginTop: "20px" }}>
                  Estimated IDV: ₹ {premium.idv.toLocaleString("en-IN")}
                </h3>
                <h3 style={{ marginTop: "10px" }}>
                  Estimated Annual Premium: ₹ {premium.annual.toLocaleString("en-IN")}
                </h3>
                <p style={noteStyle}>
                  This is an estimated value. Actual insurance premium depends on insurer, add-ons, claim history, and bike model.
                </p>
                <button onClick={shareOnWhatsApp} 
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm">
                  Share on WhatsApp
                </button>
                <button onClick={downloadPDF}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm">
                  Download PDF
                </button>
              </>
            )}
          </div>
        </CalculatorCard>
      </div>
    </>
  );
}

// Styles
const inputStyle = {
  width: "100%",
  padding: "12px",
  marginBottom: "10px",
  borderRadius: "10px",
  border: "1px solid #333",
  background: "#1a1a1a",
  color: "#fff",
};

const buttonStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "10px",
  border: "none",
  background: "linear-gradient(90deg, #ff4d00, #ff7a00)",
  color: "#fff",
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: "10px",
};

const noteStyle = {
  marginTop: "14px",
  color: "#aaa",
  lineHeight: 1.6,
  fontSize: "14px",
};

const seoBox = {
  marginBottom: "20px",
  background: "#111",
  padding: "15px",
  borderRadius: "10px",
  border: "1px solid #222",
};

const seoText = {
  color: "#aaa",
  lineHeight: "1.6",
  marginTop: "8px",
};